import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action } from 'mobx';
import { Button, Drawer, Icon, Intent, Position, Spinner } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import { Api } from 'services/Api';
import ClientAutocomplete from 'components/ClientAutocomplete';
import Pagination from 'components/Pagination';

// Side panel (slides in from the left) that moves equipment from one client
// to another, e.g. after a property changes hands: pick the two clients, tick
// equipment on any page, move. Opened from
// the Equipment list (any client) and from a client's page, where `lockSource`
// fixes "From" to that client.
//   POST /clients/:source_id/move_equipment { equipment_ids, target_client_id }
// The modal loads its own equipment list, so it never touches the Equipment
// page's results, and only the latest request may fill it: a slow answer for
// a client picked earlier can't show up under the one picked now.
// Styles: .bmv in styles/move-equipment.scss.

const PER_PAGE = 25;

const equipmentName = (attributes) => attributes.kind || attributes.serial_number || 'Equipment';
const plural = (count) => (count === 1 ? 'item' : 'items');

@inject('clientStore')
@observer
class MoveEquipmentPanel extends Component {
  @observable source = null;          // { id, name }
  @observable target = null;          // { id, name }
  @observable equipment = [];
  @observable meta = null;
  @observable page = 1;
  @observable loading = false;
  @observable loadError = null;
  @observable selected = new Map();   // id -> name, across pages
  @observable moving = false;
  @observable moveError = null;
  requestId = 0;

  componentDidMount() {
    if (this.props.isOpen) this.start();
  }

  // The panel stays mounted (so it can slide in and out); each opening starts fresh.
  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) this.start();
  }

  @action start() {
    const { sourceClientId, sourceClientName } = this.props;
    this.requestId += 1;
    this.source = null;
    this.target = null;
    this.equipment = [];
    this.meta = null;
    this.loading = false;
    this.loadError = null;
    this.selected = new Map();
    this.moving = false;
    this.moveError = null;
    if (!sourceClientId) return;

    this.source = { id: sourceClientId, name: sourceClientName || null };
    this.load(1);
    if (sourceClientName) return;
    Api.get(`/clients/${sourceClientId}`)
      .then(action((response) => {
        const client = response.data && response.data.data;
        if (this.source && this.source.id === sourceClientId && client) {
          this.source = { id: sourceClientId, name: client.attributes.name };
        }
      }))
      .catch(() => {});
  }

  @action load(page = 1) {
    if (!this.source) return;
    const requestId = ++this.requestId;
    const clientId = this.source.id;
    this.page = page;
    this.loading = true;
    this.loadError = null;
    Api.post('/equipments/search', {
      search: { where: { client_id: clientId }, order: { created_at: 'desc' }, per_page: PER_PAGE, page }
    })
      .then(action((response) => {
        if (requestId !== this.requestId) return;
        this.equipment = response.data.data || [];
        this.meta = response.data.meta || null;
      }))
      .catch(action(() => {
        if (requestId !== this.requestId) return;
        this.equipment = [];
        this.loadError = 'The equipment couldn\'t be loaded.';
      }))
      .then(action(() => {
        if (requestId === this.requestId) this.loading = false;
      }));
  }

  @action onSourceSelect = (client) => {
    this.selected = new Map();
    this.equipment = [];
    this.meta = null;
    this.moveError = null;
    this.requestId += 1; // drop anything still loading for the previous client
    this.loading = false;
    this.source = client ? { id: client.id, name: client.attributes.name } : null;
    if (this.source) this.load(1);
  };

  @action onTargetSelect = (client) => {
    this.moveError = null;
    this.target = client ? { id: client.id, name: client.attributes.name } : null;
  };

  get pageIds() {
    return this.equipment.map((item) => item.id);
  }

  get pageAllSelected() {
    const ids = this.pageIds;
    return ids.length > 0 && ids.every((id) => this.selected.has(id));
  }

  get pageSomeSelected() {
    return this.pageIds.some((id) => this.selected.has(id));
  }

  @action togglePage = () => {
    if (this.pageAllSelected) {
      this.equipment.forEach((item) => this.selected.delete(item.id));
    } else {
      this.equipment.forEach((item) => this.selected.set(item.id, equipmentName(item.attributes)));
    }
  };

  @action toggle(item) {
    if (this.selected.has(item.id)) this.selected.delete(item.id);
    else this.selected.set(item.id, equipmentName(item.attributes));
  }

  @action clearSelection = () => {
    this.selected = new Map();
  };

  get sameClient() {
    return Boolean(this.source && this.target && String(this.source.id) === String(this.target.id));
  }

  // What the footer says: what's missing, or exactly what will happen.
  get summary() {
    const count = this.selected.size;
    if (!this.source) return 'Choose the client the equipment belongs to.';
    if (count === 0) return 'Tick the equipment to move.';
    if (!this.target) return `Choose the client to move ${count === 1 ? 'it' : 'them'} to.`;
    if (this.sameClient) return 'Choose a different client to move to.';
    return `${count} ${plural(count)} will move from ${this.source.name || 'this client'} to ${this.target.name}.`;
  }

  get canMove() {
    return Boolean(this.source && this.target && !this.sameClient && this.selected.size > 0 && !this.moving);
  }

  @action move = () => {
    if (!this.canMove) return;
    const ids = Array.from(this.selected.keys());
    const target = this.target;
    this.moving = true;
    this.moveError = null;
    this.props.clientStore
      .moveEquipment(this.source.id, { equipment_ids: ids, target_client_id: target.id })
      .then(action(() => {
        this.moving = false;
        Toast.show({ icon: 'tick', message: `Moved ${ids.length} ${plural(ids.length)} to ${target.name}.`, intent: Intent.SUCCESS });
        this.props.onSuccess({ movedCount: ids.length, targetClientId: target.id });
      }))
      .catch(action((error) => {
        this.moving = false;
        const body = error && error.response && error.response.data;
        const apiError = body && (body.error || body.errors);
        this.moveError = Array.isArray(apiError) ? apiError.join(', ') : (apiError || 'Nothing was moved. Try again.');
      }));
  };

  renderClientSlot({ id, label, client, selectedClientId, onSelect, placeholder, isTarget, locked }) {
    if (locked) {
      return (
        <div className="bmv__slot is-set bmv__slot--locked">
          <span className="bmv__slot-label">{label}</span>
          <span className="bmv__slot-fixed" title={client && client.name}>{(client && client.name) || 'This client'}</span>
        </div>
      );
    }
    return (
      <div className={`bmv__slot${client ? ' is-set' : ''}${isTarget ? ' bmv__slot--to' : ''}`}>
        <label className="bmv__slot-label" htmlFor={id}>{label}</label>
        <ClientAutocomplete
          selectedClientId={selectedClientId}
          inputProps={{ id, placeholder, leftIcon: 'search', icon: undefined }}
          onClientSelect={onSelect}
        />
      </div>
    );
  }

  renderList() {
    if (!this.source) {
      return (
        <div className="bmv__empty">
          <Icon icon="box" iconSize={20} />
          <p>Choose the client the equipment belongs to, and their equipment shows up here.</p>
        </div>
      );
    }
    if (this.loading && this.equipment.length === 0) {
      return <div className="bmv__empty"><Spinner size={22} /><p>Loading equipment…</p></div>;
    }
    if (this.loadError) {
      return (
        <div className="bmv__empty bmv__empty--error" role="alert">
          <Icon icon="error" iconSize={20} />
          <p>{this.loadError}</p>
          <Button className="bmv__btn" icon="refresh" onClick={() => this.load(this.page)}>Try again</Button>
        </div>
      );
    }
    if (this.equipment.length === 0) {
      return (
        <div className="bmv__empty">
          <Icon icon="box" iconSize={20} />
          <p>{this.source.name || 'This client'} has no equipment to move.</p>
        </div>
      );
    }

    const total = this.meta && this.meta.total_records;
    return (
      <React.Fragment>
        <div className="bmv__list-head">
          <label className="bmv__check">
            <input
              type="checkbox"
              checked={this.pageAllSelected}
              ref={(input) => { if (input) input.indeterminate = !this.pageAllSelected && this.pageSomeSelected; }}
              onChange={this.togglePage}
            />
            <span>Select all on this page</span>
          </label>
          <span className="bmv__count" aria-live="polite">
            {this.selected.size > 0 ? (
              <React.Fragment>
                {this.selected.size} selected{total ? ` of ${total}` : ''}
                <button type="button" className="bmv__link" onClick={this.clearSelection}>Clear</button>
              </React.Fragment>
            ) : (total ? `${total} ${plural(total)}` : null)}
          </span>
        </div>
        <ul className={`bmv__list${this.loading ? ' is-loading' : ''}`} aria-busy={this.loading ? 'true' : undefined}>
          {this.equipment.map((item) => {
            const attributes = item.attributes;
            const name = equipmentName(attributes);
            const checked = this.selected.has(item.id);
            const serial = attributes.serial_number ? `SN ${attributes.serial_number}` : 'No serial number';
            const image = attributes.images && attributes.images.length > 0 && attributes.images[0].asset && attributes.images[0].asset.thumb
              ? attributes.images[0].asset.thumb.url
              : '/../equipment_default.jpg';
            const address = attributes.location && attributes.location.full_address;
            return (
              <li key={item.id}>
                <label className={`bmv__row${checked ? ' is-checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => this.toggle(item)}
                    aria-label={`${name}, ${serial}${attributes.make ? `, ${attributes.make}` : ''}${attributes.eq_model ? ` ${attributes.eq_model}` : ''}`}
                  />
                  <img className="bmv__thumb" src={image} alt="" />
                  <span className="bmv__cell">
                    <strong>{name}</strong>
                    {attributes.kind && <span>{serial}</span>}
                    <span className={attributes.make || attributes.eq_model ? '' : 'is-empty'}>
                      {[attributes.make, attributes.eq_model].filter(Boolean).join(' ') || 'No make or model'}
                    </span>
                  </span>
                  <span className="bmv__cell">
                    <span className={`bmv__where${attributes.eq_location ? '' : ' is-empty'}`}>
                      <Icon icon="map-marker" iconSize={12} />{attributes.eq_location || 'No location'}
                    </span>
                    {address && <span className="bmv__address" title={address}>{address}</span>}
                    {attributes.description && <span className="bmv__description" title={attributes.description}>{attributes.description}</span>}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        {this.meta && this.meta.total_pages > 1 && (
          <div className="bmv__pages">
            <Pagination value={this.meta.page} total={this.meta.total_pages} onChange={(page) => this.load(page)} />
          </div>
        )}
      </React.Fragment>
    );
  }

  render() {
    const count = this.selected.size;
    return (
      <Drawer
        className="bmv"
        title="Move equipment"
        icon="swap-horizontal"
        position={Position.LEFT}
        size="min(720px, 100vw)"
        isOpen={this.props.isOpen}
        onClose={this.moving ? undefined : this.props.onClose}
        canOutsideClickClose={!this.moving}
        canEscapeKeyClose={!this.moving}
      >
        <div className="bmv__body">
          <p className="bmv__intro">Move equipment from one client to another, for example after a property changes hands.</p>

          <div className="bmv__transfer">
            {this.renderClientSlot({
              id: 'bmv-from',
              label: 'From client',
              client: this.source,
              selectedClientId: this.props.sourceClientId,
              onSelect: this.onSourceSelect,
              placeholder: 'Search clients',
              locked: this.props.lockSource
            })}
            <div className={`bmv__connector${count > 0 ? ' is-active' : ''}`} aria-hidden="true">
              <span className="bmv__connector-line" />
              <span className="bmv__connector-count">{count > 0 ? `${count} ${plural(count)}` : <Icon icon="arrow-down" iconSize={14} />}</span>
            </div>
            {this.renderClientSlot({
              id: 'bmv-to',
              label: 'To client',
              client: this.target,
              onSelect: this.onTargetSelect,
              placeholder: 'Search clients',
              isTarget: true
            })}
          </div>

          <section className="bmv__panel" aria-label="Equipment to move">
            {this.renderList()}
          </section>
        </div>

        <div className="bmv__footer">
          <p className={`bmv__summary${this.moveError || this.sameClient ? ' is-error' : ''}`} role={this.moveError ? 'alert' : undefined}>
            {this.moveError || this.summary}
          </p>
          <div className="bmv__actions">
            <Button className="bmv__btn" onClick={this.props.onClose} disabled={this.moving}>Cancel</Button>
            <Button
              className="bmv__btn bmv__btn--primary"
              intent={Intent.PRIMARY}
              loading={this.moving}
              disabled={!this.canMove}
              onClick={this.move}
            >
              {count > 0 ? `Move ${count} ${plural(count)}` : 'Move equipment'}
            </Button>
          </div>
        </div>
      </Drawer>
    );
  }
}

export default MoveEquipmentPanel;
